import { OP_LEVELS_ASCENDING, OP_FULL_NAMES } from "@/config/operatingPoints";
import { Button } from "@/design-system/components/react";
import type { FindingJumpTarget } from "@/hooks/useReportQuery";
import { neuroFindingLabel, neuroOutcome, type NeuroReview } from "@/types/neuroReview";

interface FindingNavigation {
  findingTargets?: Record<string, FindingJumpTarget>;
  onFindingSelect?: (condition: string, target: FindingJumpTarget) => void;
}

function DecisionTable({
  result,
  unavailable,
  findingTargets,
  onFindingSelect,
}: { result: NeuroReview; unavailable: boolean } & FindingNavigation) {
  const entries = Object.entries(result.predictions ?? {})
    .filter(([, prediction]) => (prediction.predicted_class === null) === unavailable)
    .sort(
      ([a, pa], [b, pb]) =>
        Number(pb.predicted_class) - Number(pa.predicted_class) || a.localeCompare(b),
    );
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <caption className="sr-only">Neuro model decisions and operating points</caption>
        <thead>
          <tr className="border-b border-border text-left">
            <th scope="col" className="p-2">
              Finding
            </th>
            <th scope="col" className="p-2">
              Decision
            </th>
          </tr>
        </thead>
        <tbody>
          {entries.map(([condition, prediction]) => (
            <tr key={condition} className="border-b border-border align-top">
              <th scope="row" className="p-2 text-left font-normal">
                {prediction.predicted_class === true &&
                findingTargets?.[condition] &&
                onFindingSelect ? (
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto p-0 text-left text-xs whitespace-normal capitalize underline"
                    aria-label={`Jump to ${neuroFindingLabel(condition)}`}
                    onClick={() => onFindingSelect(condition, findingTargets[condition])}
                  >
                    {neuroFindingLabel(condition)}
                  </Button>
                ) : (
                  <>
                    <span className="capitalize">{neuroFindingLabel(condition)}</span>
                    {prediction.predicted_class === true && (
                      <p className="mt-1 text-fg-muted">Image location unavailable</p>
                    )}
                  </>
                )}
                <details className="mt-1 text-fg-muted">
                  <summary className="cursor-pointer">Operating points</summary>
                  <dl className="mt-2 space-y-1">
                    {OP_LEVELS_ASCENDING.map((level) => (
                      <div key={level} className="flex justify-between gap-3">
                        <dt>{OP_FULL_NAMES[level]}</dt>
                        <dd>{prediction.operating_points[level] ? "True" : "False"}</dd>
                      </div>
                    ))}
                  </dl>
                </details>
              </th>
              <td className="p-2 whitespace-nowrap">
                {prediction.predicted_class === null
                  ? "No decision"
                  : prediction.predicted_class
                    ? "Positive"
                    : "Negative"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function NeuroReviewPanel({
  result,
  loading,
  error,
  findingTargets,
  onFindingSelect,
}: {
  result?: NeuroReview;
  loading: boolean;
  error: boolean;
} & FindingNavigation) {
  if (error)
    return (
      <p role="alert" className="p-4">
        Could not load the Neuro result.
      </p>
    );
  if (loading)
    return (
      <p role="status" className="p-4">
        Loading Neuro result…
      </p>
    );
  if (!result)
    return (
      <p role="alert" className="p-4">
        Neuro review data is unavailable.
      </p>
    );
  const gate = result.study_gate;
  const unavailable = Object.values(result.predictions ?? {}).filter(
    (p) => p.predicted_class === null,
  ).length;
  return (
    <section aria-label="Neuro results" className="h-full overflow-y-auto bg-bg text-fg">
      <header className="p-3 border-b border-border">
        <h2 className="font-semibold">Neuro results</h2>
        <p className="mt-1 text-sm">{neuroOutcome(result)}</p>
        <p className="mt-2 text-xs text-fg-muted">
          Study gate: {gate.operating_points[gate.selected_operating_point] ? "Passed" : "Rejected"}
          {" · "}
          {OP_FULL_NAMES[gate.selected_operating_point]}
        </p>
      </header>
      {result.successful_inference ? (
        <>
          <p className="p-3 text-xs text-fg-muted">
            Decisions are shown as emitted by the model. The study gate operating point does not
            select a finding operating point.
          </p>
          <DecisionTable
            result={result}
            unavailable={false}
            findingTargets={findingTargets}
            onFindingSelect={onFindingSelect}
          />
          {unavailable > 0 && (
            <details className="m-3 border border-dashed border-border-strong">
              <summary className="p-2 cursor-pointer text-sm">
                {unavailable} entries with no decision
              </summary>
              <p className="p-2 text-xs text-fg-muted">
                These entries are not negative model decisions.
              </p>
              <DecisionTable result={result} unavailable />
            </details>
          )}
        </>
      ) : (
        <p className="p-3 text-sm">No finding decisions were produced for this study.</p>
      )}
    </section>
  );
}
